import { Link } from "react-router-dom";
import { ChevronLeft, ChevronUp, ChevronDown, Plus, BookOpen, Lock, EyeOff, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DraftBar } from "@/components/studio/DraftBar";
import { DesktopHint } from "@/components/studio/DesktopOnly";
import { chapterLabel, fmtNum } from "@/lib/studio";

const move = (items, i, d) => { const next = [...items]; [next[i], next[i + d]] = [next[i + d], next[i]]; return next; };

const Row = ({ it, n, link, editable, up, down }) => {
  const scene = it.kind === "scene";
  const body = (
    <div className="min-w-0 flex-1">
      <div className={`truncate ${scene ? "text-sm italic text-[#A8A090]" : "text-[#F3EBDD]"}`}>{scene ? <><StickyNote className="mr-1 inline h-3.5 w-3.5" />{it.title || "Scene note"}</> : chapterLabel(n, it.title)}</div>
      {!scene && <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[#A8A090]">{fmtNum(it.word_count || 0)} words{it.hidden && <span className="flex items-center gap-0.5"><EyeOff className="h-3 w-3" />hidden</span>}</div>}
    </div>
  );
  return (
    <li className="flex items-center gap-2 rounded-md border border-[#2D3442] bg-[#13171F] px-3 py-3" data-testid={`mobile-item-${it.id}`}>
      {scene ? body : <Link to={link(it)} className="flex min-w-0 flex-1" data-testid={`mobile-chapter-link-${it.id}`}>{body}</Link>}
      {editable && <div className="flex flex-col">
        <button onClick={up} disabled={!up} className="rounded p-1 text-[#A8A090] disabled:opacity-25" aria-label="Move up" data-testid={`mobile-move-up-${it.id}`}><ChevronUp className="h-4 w-4" /></button>
        <button onClick={down} disabled={!down} className="rounded p-1 text-[#A8A090] disabled:opacity-25" aria-label="Move down" data-testid={`mobile-move-down-${it.id}`}><ChevronDown className="h-4 w-4" /></button>
      </div>}
    </li>
  );
};

export const MobileStory = ({ story, base, sid, items, draft, draftId, editable, setPicked, refresh, reorder, addChapter, loaded }) => {
  const chapters = items.filter((i) => i.kind === "chapter");
  const link = (it) => `${base}/studio/${sid}/c/${it.id}`;
  let n = 0;
  return (
    <div className="space-y-5 px-4 pb-28 pt-6" data-testid="mobile-story-page">
      <Link to={`${base}/studio`} className="inline-flex items-center gap-1 text-xs text-[#A8A090]" data-testid="back-to-studio"><ChevronLeft className="h-3.5 w-3.5" />All stories</Link>
      <div>
        <h1 className="font-display text-4xl leading-tight text-[#F3EBDD] [overflow-wrap:anywhere]" data-testid="story-title">{story.title}</h1>
        {story.synopsis && <p className="mt-1 text-sm text-[#A8A090]">{story.synopsis}</p>}
        <div className="mt-2 flex gap-4 text-xs text-[#A8A090]" data-testid="story-stats"><span><b className="text-[#F3EBDD]">{fmtNum(chapters.reduce((a, c) => a + (c.word_count || 0), 0))}</b> words</span><span><b className="text-[#F3EBDD]">{chapters.length}</b> chapters</span></div>
      </div>
      <div className="[&_button[data-testid=new-draft-button]]:h-9 [&_[data-testid=draft-select]]:w-full">
        <DraftBar story={story} draftId={draftId} onSelect={setPicked} onCreated={(id) => { setPicked(id); refresh(); }} compareLink={`${base}/studio/${sid}/compare`} />
      </div>
      <div className="flex gap-2">
        {editable && <Button className="flex-1" onClick={addChapter} data-testid="mobile-add-chapter"><Plus className="mr-1 h-4 w-4" />New chapter</Button>}
        {chapters.length > 0 && <Button variant="outline" className="flex-1" asChild><Link to={`/read/${sid}`} data-testid="mobile-open-reader"><BookOpen className="mr-1 h-4 w-4" />Reader mode</Link></Button>}
      </div>
      {draft?.locked && <p className="flex items-center gap-1.5 text-xs text-[#FBBF24]" data-testid="draft-locked-note"><Lock className="h-3.5 w-3.5" />{draft.name} is read-only.</p>}
      {loaded && items.length === 0 && <p className="rounded-md border border-dashed border-[#2D3442] p-8 text-center text-sm text-[#A8A090]" data-testid="board-empty">{editable ? "Add your first chapter to begin." : "Nothing here yet."}</p>}
      <ul className="space-y-2" data-testid="mobile-chapter-list">
        {items.map((it, i) => {
          if (it.kind === "chapter") n += 1;
          return <Row key={it.id} it={it} n={n} link={link} editable={editable} up={i > 0 ? () => reorder(move(items, i, -1)) : null} down={i < items.length - 1 ? () => reorder(move(items, i, 1)) : null} />;
        })}
      </ul>
      {story.role === "owner" && <DesktopHint>The storyboard, scene notes, book details, publishing, stats and PDF/EPUB export open on desktop.</DesktopHint>}
    </div>
  );
};
